import { addMinutes } from 'date-fns';
import { format } from 'date-fns-tz';
import React, { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';
import TableLoading from '@components/common/TableLoading/TableLoading';
import TableLoadingTrigger from '@components/common/TableLoadingTrigger/TableLoadingTrigger';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import useEquipmentsLog from '../../hooks/useEquipmentsLog';
import { selectManageEquipmentSearch } from '../../store/useManageEquipmentStore';
import TableEquipmentsLogSkeleton from './TableEquipmentsLogSkeleton';
import style from './style.module.scss';

const TableEquipmentsLog = () => {
  const { t } = useTranslation();
  const { data: profileData } = useUserProfileQuery();

  const dateTimeFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  const equipmentSearch = selectManageEquipmentSearch();

  const [debouncedValue] = useDebouncedValue(equipmentSearch, 500);

  const {
    fetchNextPage,
    isFetching,
    hasNextPage,
    isLoading,
    equipmentsLog: logs,
  } = useEquipmentsLog({
    searchValue: debouncedValue,
  });

  const usersTimezone = -new Date().getTimezoneOffset();

  const [ref, isInViewPort] = useIntersectionObserver({
    threshold: 0.1,
    reInitializeOnChange: isFetching,
  });

  useEffect(() => {
    if (isInViewPort && hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [isInViewPort, hasNextPage, isFetching]);

  return (
    <div className={style.equipmentsLogsWrapper}>
      <div className={style.equipmentsLogsTableWrapper}>
        <table className={style.equipmentLogsTable} cellSpacing="0" cellPadding="0">
          <thead>
            <tr>
              <th>{t('Equipment')}</th>
              <th>{t('Event')}</th>
              <th>{t('Username')}</th>
              <th>
                {t('Created At')}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <TableEquipmentsLogSkeleton />
            ) : logs.length ? (
              logs.map((row) => (
                <tr key={uuidv4()} className={style.equipmentLogsTableBodyRow}>
                  <td>{row.equipment_name}</td>
                  <td>
                    <div className={style.messageCell}>{row.message}</div>
                  </td>
                  <td>{row.user_name}</td>
                  <td className={style.uppercaseCell}>
                    {format(addMinutes(row.created_at, usersTimezone), dateTimeFormat)}
                  </td>
                </tr>
              ))
            ) : (
              <TableEmptyRowExtended text={t('No equipments log were found')} />
            )}
            <TableLoadingTrigger show={!isFetching} ref={ref} />
            <TableLoading show={isFetching} />
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableEquipmentsLog;
